import { z } from 'zod';
import { cuidSchema } from './common.js';

/**
 * Contratos del portafolio.
 *
 * El CATÁLOGO de proyectos (qué existe, qué objetivo tiene, en qué mes se
 * entrega) vive en `content/portfolio/projects.js`, porque es contenido y se
 * versiona en Git. Lo que vive aquí es el CONTRATO: la forma que ese catálogo
 * debe cumplir y la forma del estado que el usuario guarda sobre cada proyecto.
 *
 * La separación importa: si el catálogo estuviera en la base de datos, cada
 * cambio de proyecto sería una migración. Al ser código, es un commit.
 */

/**
 * Un proyecto DEL CATÁLOGO, tal y como está en `content/portfolio/projects.js`.
 *
 * Se llama `portfolioCatalogItemSchema` y no `portfolioProjectSchema` a
 * propósito: ese nombre lo usa `progress.js` para la RESPUESTA de la API, que
 * es otra cosa (lleva `status`, `repoUrl`, `notes` y `updatedAt`). Cuando dos
 * archivos del barril exportan el mismo nombre, ESM no elige uno: descarta el
 * nombre entero y `import { portfolioProjectSchema }` falla al enlazar. Pasó
 * aquí: el nombre llevaba meses sin exportarse y nadie lo notó porque nadie lo
 * importaba.
 */
export const portfolioCatalogItemSchema = z.object({
  projectKey: z
    .string()
    .min(1)
    .regex(/^p[1-4]-[a-z0-9-]+$/, 'projectKey debe tener el formato p1-... a p4-...'),
  title: z.string().min(1),
  goal: z.string().min(1),
  suggestedStack: z.array(z.string()),
  month: z.number().int().min(1).max(12),
});

export const PROJECTS = z.array(portfolioCatalogItemSchema).length(4);

/**
 * Estado que el usuario guarda sobre un proyecto.
 *
 * `month` y `title` NO están aquí a propósito: vienen del catálogo. Duplicarlos
 * en la base de datos es la forma más fácil de que acaben discrepando ("¿el
 * proyecto se llama 'API de tareas' o 'API REST de tareas'?"). La fila solo
 * guarda lo que el usuario añade.
 */
export const portfolioEntrySchema = z.object({
  projectKey: z.string().min(1),
  status: z.enum(['PLANNED', 'IN_PROGRESS', 'DONE']),
  repositoryUrl: z.url().optional(),
  liveUrl: z.url().optional(),
  notes: z.string().max(5000).optional(),
  completedAt: z.coerce.date().optional(),
});

export const upsertPortfolioEntrySchema = z
  .object({
    status: z.enum(['PLANNED', 'IN_PROGRESS', 'DONE']),
    repositoryUrl: z.url().optional(),
    liveUrl: z.url().optional(),
    notes: z.string().max(5000).optional(),
  })
  .refine((value) => value.repositoryUrl || value.liveUrl || value.notes, {
    message: 'Añade al menos una URL, un enlace al repositorio o notas.',
  });

export const portfolioProjectIdSchema = z.object({ id: cuidSchema });
